import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges,
  OnDestroy, OnInit, SimpleChanges,
} from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { MatDialog } from '@angular/material/dialog';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Subject, combineLatest } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import {
  FeatureSettings, TenantFeatures, isFeatureEnabled, issueCasesEnabled,
} from 'src/app/models/featureSettings';
import { TenantSessionState } from 'src/app/store/state/tenant-session.state';
import { IssuesStateService } from '../issues-state.service';
import { IssueDto } from '../models/issue';
import { IssueRowDisplay } from '../shared/issue-row/issue-row-display';
import { IssueRowVm, WriteStatuses } from '../models/issue-view';
import { WIDGET_TOP_N, buildRowVm, topNBySeverity } from '../issue-formatting';
import {
  ReportIssueDialogComponent, ReportIssueDialogData, ReportIssueDialogResult,
} from '../report-issue-dialog/report-issue-dialog.component';

// Issues summary card for a single vehicle's Summary tab. Reads the open-issue
// list from IssuesStateService (which also owns async writes) and renders it as a
// flat accordion — top-N by severity, each row expanding in place into the full
// issue detail card (one open at a time; no separate drawer).
@Component({
  selector: 'app-issues-widget',
  templateUrl: './issues-widget.component.html',
  styleUrls: ['./issues-widget.component.scss'],
  animations: [
    // Accordion open/close. The detail wrapper enters (open) / leaves (close) the
    // DOM; animate its height between 0 and natural height with a fade. overflow
    // is clipped during the transition only (Angular clears the inline style
    // afterward). Mirrors the app's dynamic-table detailExpand easing.
    trigger('detailExpand', [
      transition(':enter', [
        style({ height: '0', opacity: 0, overflow: 'hidden' }),
        animate('200ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ height: '*', opacity: 1 })),
      ]),
      transition(':leave', [
        style({ overflow: 'hidden' }),
        animate('180ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ height: '0', opacity: 0 })),
      ]),
    ]),
    // Issue leaving the list (dismiss / mark fixed / any disposition close):
    // fade + collapse the card out instead of an instant disappear.
    trigger('rowLeave', [
      transition(':leave', [
        style({ overflow: 'hidden' }),
        animate('240ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ height: '0', opacity: 0 })),
      ]),
    ]),
  ],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IssuesWidgetComponent implements OnInit, OnChanges, OnDestroy {
  @Input() assetId!: string;
  @Input() assetName = '';
  @Input() assetVin = '';
  // The asset's group memberships — scope the case workflow's assignee picker.
  @Input() assetGroups: any[] = [];
  @Input() canMaintain = false;
  @Input() currentOdometer?: number;
  @Input() currentEngineHours?: number;

  readonly WriteStatuses = WriteStatuses;

  @SelectSnapshot(TenantSessionState.getTenantFeatures) private tenantFeatures!: FeatureSettings;

  // Reporting needs the screens to be on and MaintainAlert. The screens are what
  // IssuesUI governs; the pipeline that receives the report runs under its own flag.
  get canReport(): boolean {
    return this.canMaintain && isFeatureEnabled(this.tenantFeatures, TenantFeatures.IssuesUI);
  }

  // Whether Issues show cases on this tenant. The collapsed row's assignee follows it; the
  // expanded card's own detail reads the same setting for itself.
  get showCase(): boolean {
    return issueCasesEnabled(this.tenantFeatures);
  }

  loading = false;
  error: string | null = null;

  // Accordion (one open at a time). `expandedId` drives which detail is open;
  // `animatingId` stays set through the close animation so the collapsed row is
  // held back until the shrinking detail card has fully left its slot.
  expandedId: string | null = null;
  animatingId: string | null = null;

  // Matches the detailExpand :leave duration.
  private static readonly LEAVE_MS = 180;

  openCount = 0;
  shownRows: IssueRowVm[] = [];
  moreCount = 0;

  readonly skeletonRows = [0, 1, 2];

  private allRows: IssueRowVm[] = [];
  private readonly assetChanged$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly state: IssuesStateService,
    private readonly dialog: MatDialog,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void { this.subscribe(); }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.assetId && !changes.assetId.firstChange) {
      this.expandedId = null;
      this.animatingId = null;
      this.subscribe();
    }
  }

  ngOnDestroy(): void {
    this.assetChanged$.next();
    this.assetChanged$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private subscribe(): void {
    if (!this.assetId) return;
    this.assetChanged$.next(); // drop prior asset's subscriptions

    combineLatest([
      this.state.issuesFor(this.assetId),
      this.state.loadingFor(this.assetId),
      this.state.errorFor(this.assetId),
    ]).pipe(takeUntil(this.assetChanged$), takeUntil(this.destroy$))
      .subscribe(([issues, loading, error]) => {
        this.rebuild(issues);
        this.loading = loading;
        this.error = error;
        this.cdr.markForCheck();
      });

    this.state.load(this.assetId);
  }

  private rebuild(issues: IssueDto[]): void {
    this.allRows = (issues ?? []).map(i => buildRowVm(i, id => this.state.resolveUserName(id)));
    this.openCount = this.allRows.length;
    this.shownRows = topNBySeverity(this.allRows, WIDGET_TOP_N);
    this.moreCount = Math.max(0, this.allRows.length - this.shownRows.length);
    // A closed/removed issue can no longer stay expanded.
    if (this.expandedId && !this.allRows.some(r => r.id === this.expandedId)) {
      this.expandedId = null;
    }
    if (this.animatingId && !this.allRows.some(r => r.id === this.animatingId)) {
      this.animatingId = null;
    }
  }

  writeStatusFor(issueId: string) { return this.state.writeStatusFor(issueId); }

  // The user asked again after a failure, so this one goes to the server.
  retry(): void { this.state.load(this.assetId, true); }

  // The dialog matches consolidation against the open issues we already hold, so
  // hand it the current snapshot rather than making it fetch them again.
  reportIssue(): void {
    if (!this.canReport || !this.assetId) return;
    const dialogRef = this.dialog.open(ReportIssueDialogComponent, {
      data: {
        assetId: this.assetId,
        assetName: this.assetName,
        assetVin: this.assetVin,
        openIssues: this.state.snapshotFor(this.assetId),
      } as ReportIssueDialogData,
      panelClass: 'issue-report-dialog',
      width: '560px',
      maxWidth: '95vw',
      autoFocus: false,
      // A click beside the dialog or Escape would drop what the person typed. The
      // dialog closes only through its own close button, Cancel, or a report.
      disableClose: true,
    });

    dialogRef.afterClosed().pipe(take(1), takeUntil(this.destroy$))
      .subscribe((result?: ReportIssueDialogResult) => {
        if (result?.requestId) this.state.reconcileCreate(this.assetId, result.requestId);
      });
  }

  // --- Accordion -----------------------------------------------------------

  isExpanded(id: string): boolean { return this.expandedId === id; }

  // The collapsed row is hidden while its detail is open OR animating closed.
  showCollapsed(id: string): boolean { return this.animatingId !== id; }

  // Takes the row's display shape rather than the full view-model: the shared row is
  // rendered by every surface now, and only the id is needed here.
  toggleRow(row: IssueRowDisplay): void {
    if (this.expandedId === row.id) { this.startClose(row.id); return; }
    this.expandedId = row.id;
    this.animatingId = row.id;
  }

  collapseRow(): void {
    if (this.expandedId) this.startClose(this.expandedId);
  }

  // Close: clear expandedId (detail plays its :leave), then release the collapsed
  // row once the leave has finished so the two never overlap in the same slot.
  private startClose(id: string): void {
    this.expandedId = null;
    setTimeout(() => {
      if (!this.expandedId && this.animatingId === id) {
        this.animatingId = null;
        this.cdr.markForCheck();
      }
    }, IssuesWidgetComponent.LEAVE_MS);
  }

  // Stable identity across rebuilds: a reconcile refetch (e.g. after a note add
  // or assignment) replaces the row VMs, so without trackBy Angular would tear
  // down and recreate the expanded card, snapping its coordination panel / open
  // editor shut mid-edit.
  trackByRowId = (_: number, r: IssueRowVm): string => r.id;
}
